import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search, RotateCcw, RefreshCw, FileSpreadsheet, LayoutGrid, List,
    Check, Users, CalendarRange, Info, AlertCircle, Clock,
    ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import Breadcrumb, { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import { fetchAllAttendanceCard, fetchHolidays } from '../../store/slices/attendanceSlice';
import { fetchLeaveRequestsByStatus, selectLeaveRequests } from '../../store/slices/leaveSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { fetchLeaveTypes, selectLeaveTypes } from '../../store/slices/leaveTypesSlice';
import { exportAttendanceOverviewToExcel } from '../../utils/attendanceOverviewExcel';

const FIELD =
    'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-gray-700 transition-shadow';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const STICKY_TOP = -24;
const STICKY_BOTTOM = -23;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => String(currentYear + 1 - i));
const MONTH_OPTIONS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const STATUS_FILTERS = [
    { value: 'all', label: 'All Days' },
    { value: 'full-day', label: 'Full Day' },
    { value: 'mismatch', label: 'Mismatch' },
    { value: 'present', label: 'Present' },
    { value: 'missing-punch', label: 'Missing Punch' },
    { value: 'no-record', label: 'No Attendance Recorded' },
    { value: 'half-day', label: 'Half Day' },
    { value: 'leave', label: 'Leave' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'sunday', label: 'Sunday' },
];

const pad = (n) => String(n).padStart(2, '0');

const toYMD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const todayYMD = () => {
    const t = new Date();
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

const computeHours = (inTime, outTime) => {
    if (!inTime || !outTime) return 0;
    try {
        const [ih, im] = inTime.split(':').map(Number);
        const [oh, om] = outTime.split(':').map(Number);
        const diff = (oh * 60 + om) - (ih * 60 + im);
        return diff > 0 ? diff / 60 : 0;
    } catch { return 0; }
};
const computeOT = (inTime, outTime) => {
    const hrs = computeHours(inTime, outTime);
    return hrs > 8 ? hrs - 8 : 0;
};

/* Decimal hours*/
const fmtHours = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${Number(n.toFixed(2))} h`;
};

/* Minutes → decimal hours*/
const fmtLateHours = (minutes) => {
    const n = Number(minutes);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `${Number((n / 60).toFixed(2))} h`;
};

const toMinutes = (t) => {
    if (!t) return null;
    const m = String(t).match(/(\d{1,2}):(\d{2})/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const rowBgHover = (r) => {
    switch (r.status) {
        case 'leave': return 'group-hover:bg-blue-200';
        case 'half-leave': return 'group-hover:bg-cyan-100';
        case 'half-day': return 'group-hover:bg-indigo-100';
        case 'holiday': return 'group-hover:bg-yellow-100';
        case 'sunday': return 'group-hover:bg-rose-100';
        case 'missing-punch': return 'group-hover:bg-fuchsia-100';
        case 'no-record': return 'group-hover:bg-slate-200';
        case 'mismatch': return 'group-hover:bg-orange-100';
        case 'present': return 'group-hover:bg-teal-100';
        default: return 'group-hover:bg-slate-50';
    }
};


const MISMATCH_AFTER = 8 * 60; // After 08:00 AM
const EARLY_OUT_TO = 17 * 60; // Before 05:00 PM

const getPunchStatus = ({ inTime, outTime, isToday }) => {
    const i = toMinutes(inTime);
    const o = toMinutes(outTime);

    // Both punches missing
    if (i == null && o == null) return 'no-record';

    // Only one punch is present.
    if (i == null || o == null) {
        // Today's IN-only record is considered Present 
        if (isToday && i != null && o == null) return 'present';
        return 'missing-punch';
    }

    // Both punches exist: late IN or early OUT is a mismatch.
    if (i > MISMATCH_AFTER || o < EARLY_OUT_TO) return 'mismatch';

    return 'full-day';
};

const PUNCH_LABELS = {
    'full-day': 'Full Day',
    'present': 'Present',
    'mismatch': 'Mismatch',
    'missing-punch': 'Missing Punch',
    'no-record': 'No Attendance Recorded',
    'half-day': 'Half Day',
};

const defaultRange = () => {
    const n = new Date();
    return {
        from: `${n.getFullYear()}-${pad(n.getMonth() + 1)}-01`,
        to: todayYMD(),
    };
};

function buildRangeDays(fromDate, toDate) {
    const list = [];
    const cur = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T00:00:00`);
    if (Number.isNaN(cur.getTime()) || Number.isNaN(end.getTime()) || cur > end) return list;

    while (cur <= end) {
        const y = cur.getFullYear();
        const m = cur.getMonth() + 1;
        const d = cur.getDate();
        const dow = cur.getDay();
        list.push({
            day: d,
            dayName: DAY_NAMES_FULL[dow],
            isSunday: dow === 0,
            ymd: `${y}-${pad(m)}-${pad(d)}`,
        });
        cur.setDate(cur.getDate() + 1);
    }
    return list;
}

function InfoTip({ children }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = React.useRef(null);

    const handleEnter = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const tooltipW = 256;
        const pad = 8;
        const left = Math.min(
            Math.max(rect.left + rect.width / 2 - tooltipW / 2, pad),
            window.innerWidth - tooltipW - pad
        );
        setCoords({ top: rect.bottom + 8, left });
        setOpen(true);
    };
    const handleLeave = () => setOpen(false);

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex items-center"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
            >
                <Info size={11} className="text-slate-400 hover:text-slate-600 cursor-help" />
            </span>
            {open &&
                createPortal(
                    <span
                        role="tooltip"
                        className="pointer-events-none fixed z-[9999] w-64
                                   rounded-lg bg-slate-900 text-white text-[11px] leading-snug font-normal
                                   px-3 py-2 shadow-lg normal-case tracking-normal text-left"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {children}
                        <span
                            className="absolute w-2 h-2 bg-slate-900"
                            style={{ top: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }}
                        />
                    </span>,
                    document.body
                )}
        </>
    );
}

const COLUMNS = [
    { key: 'ServiceNo', label: 'Service No', width: 110, align: 'center', sticky: true, left: 0 },
    { key: 'Name', label: 'Name', width: 200, sticky: true, left: 110 },
    { key: 'AttDate', label: 'Date', width: 120, align: 'center', sticky: true, left: 310 },
    { key: 'dayName', label: 'Day', width: 100, sticky: true, left: 430 },
    { key: 'inTime', label: 'In Time', width: 90, align: 'center', sticky: true, left: 530 },
    { key: 'outTime', label: 'Out Time', width: 100, align: 'center', sticky: true, left: 620 },
    { key: 'statusLabel', label: 'Status', width: 160, align: 'center' },
    {
        key: 'lateMinutes', label: 'Late Hours', width: 120, align: 'center',
        info: (
            <>
                <span className="font-semibold">Late Hours</span> — duration between shift start
                and actual IN punch, shown as decimal hours
            </>
        ),
    },
    {
        key: 'totalHours', label: 'Total Hours', width: 120, align: 'center',
        info: (
            <>
                <span className="font-semibold">Total Hours</span> = Normal Shift Hours + OT Hours
                (including Pre-OT Hours and Post-OT Hours). Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'normalHours', label: 'Normal Hours', width: 130, align: 'center',
        info: (
            <>
                <span className="font-semibold">Normal Hours</span> — regular working hours
                inside the standard shift. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'otHours', label: 'OT Hours', width: 110, align: 'center',
        info: (
            <>
                <span className="font-semibold">OT Hours</span> — total overtime hours
                (Pre-OT + Post-OT) beyond the standard shift. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'preOT', label: 'Pre OT Hours', width: 130, align: 'center',
        info: (
            <>
                <span className="font-semibold">Pre-OT Hours</span> — overtime worked before the
                standard shift start. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'postOT', label: 'Post OT Hours', width: 130, align: 'center',
        info: (
            <>
                <span className="font-semibold">Post-OT Hours</span> — overtime worked after the
                standard shift end. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'holidayOT', label: 'Holiday OT Hours', width: 150, align: 'center',
        info: (
            <>
                <span className="font-semibold">Holiday OT Hours</span> — overtime worked on a
                declared holiday (Poya / Public / Mercantile). Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'sundayOT', label: 'Sunday OT Hours', width: 150, align: 'center',
        info: (
            <>
                <span className="font-semibold">Sunday OT Hours</span> — overtime worked on a
                Sunday. Shown as decimal hours.
            </>
        ),
    },
    { key: 'Department', label: 'Department', width: 150 },
    { key: 'Location', label: 'Location', width: 150 },
    { key: 'EmploymentCategory', label: 'Category', width: 180, align: 'center' },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce((s, c) => s + c.width, 0);

export default function AttendanceOverview() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isSummaryTab =
        location.pathname === '/hrm/attendance/details' ||
        location.pathname === '/hrm/attendance/summary';
    const isDetailsTab = location.pathname === '/hrm/attendance/attendance-details';
    const isOverviewTab = location.pathname === '/hrm/attendance/attendance-overview';

    const { rows, holidays, loading, error } = useSelector(
        (s) => s.attendance || { rows: [], holidays: [], loading: false, error: null }
    );
    const leaves = useSelector(selectLeaveRequests);
    const { employees: reduxEmployees } = useSelector(
        (s) => s.employees || { employees: [] }
    );
    const leaveTypes = useSelector(selectLeaveTypes);

    const [selectedMonth, setSelectedMonth] = useState(() => pad(new Date().getMonth() + 1));
    const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [sort, setSort] = useState({ key: 'AttDate', dir: 'asc' });
    const [refreshing, setRefreshing] = useState(false);

    const [dateMode, setDateMode] = useState('month');
    const [rangeFrom, setRangeFrom] = useState(() => defaultRange().from);
    const [rangeTo, setRangeTo] = useState(() => defaultRange().to);

    const headerRef = useRef(null);
    const tableScrollRef = useRef(null);
    const barRef = useRef(null);
    const syncing = useRef(false);
    const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

    const measureBar = useCallback(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const next = {
            scrollWidth: el.scrollWidth,
            hasOverflow: el.scrollWidth > el.clientWidth + 1,
        };
        setBar((prev) =>
            prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow ? prev : next
        );
    }, []);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return undefined;
        measureBar();
        const ro = new ResizeObserver(measureBar);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        window.addEventListener('resize', measureBar);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measureBar);
        };
    }, [measureBar]);

    const onTableScroll = () => {
        const body = tableScrollRef.current;
        if (headerRef.current && body) headerRef.current.scrollLeft = body.scrollLeft;
        if (syncing.current) { syncing.current = false; return; }
        if (barRef.current && body) {
            syncing.current = true;
            barRef.current.scrollLeft = body.scrollLeft;
        }
    };

    const onBarScroll = () => {
        if (syncing.current) { syncing.current = false; return; }
        if (barRef.current && tableScrollRef.current) {
            syncing.current = true;
            tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
        }
    };

    const employeeMap = useMemo(() => {
        const map = new Map();
        if (!Array.isArray(reduxEmployees)) return map;
        reduxEmployees.forEach((emp) => {
            const serviceNo = String(
                emp.HED_SERVICE_NO || emp.SERVICE_NO || emp.serviceNo || emp.HED_EMPLOYEE_ID || ''
            ).trim();
            const employeeId = String(emp.HED_EMPLOYEE_ID || emp.EMPLOYEE_ID || emp.employeeId || '').trim();
            const rec = {
                employeeId,
                serviceNo,
                name: emp.HED_REPORT_NAME ||
                    `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
                    `Employee ${serviceNo}`,
                department: emp.HDD_DEPARTMENT_NAME || emp.HED_DEPARTMENT_NAME || '',
                location: emp.HED_LOCATION_NAME || emp.HDL_LOCATION_NAME ||
                    emp.LOCATION_NAME || emp.Location || '',
                designation: emp.HED_DESIGNATION_NAME || '',
                category: emp.HED_EMPLOYMENT_CATEGORY || emp.EMPLOYMENT_CATEGORY || '',
            };
            if (serviceNo) map.set(serviceNo, rec);
            if (employeeId && !map.has(employeeId)) map.set(employeeId, rec);
        });
        return map;
    }, [reduxEmployees]);

    useEffect(() => {
        dispatch(fetchAllEmployees());
        dispatch(fetchHolidays());
        dispatch(fetchLeaveTypes());
        dispatch(fetchLeaveRequestsByStatus('APPROVED'));
    }, [dispatch]);

    useEffect(() => {
        if (dateMode === 'month') {
            if (!selectedYear || !selectedMonth) return;
            setHasSearched(true);
            dispatch(fetchAllAttendanceCard({ month: `${selectedYear}-${selectedMonth}` }));
        } else {
            if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return;
            setHasSearched(true);
            dispatch(fetchAllAttendanceCard({ fromDate: rangeFrom, toDate: rangeTo }));
        }
    }, [selectedYear, selectedMonth, dateMode, rangeFrom, rangeTo, dispatch]);

    const handleSearch = useCallback(() => {
        if (dateMode === 'month') {
            setHasSearched(true);
            dispatch(fetchAllAttendanceCard({ month: `${selectedYear}-${selectedMonth}` }));
        } else {
            if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return;
            setHasSearched(true);
            dispatch(fetchAllAttendanceCard({ fromDate: rangeFrom, toDate: rangeTo }));
        }
    }, [selectedYear, selectedMonth, dateMode, rangeFrom, rangeTo, dispatch]);

    const handleRefresh = useCallback(async () => {
        if (refreshing || loading) return;
        setRefreshing(true);
        try {
            setHasSearched(true);
            const fetches = [
                dispatch(fetchLeaveRequestsByStatus('APPROVED')).unwrap(),
                dispatch(fetchHolidays()).unwrap(),
                dispatch(fetchLeaveTypes()).unwrap(),
            ];

            if (dateMode === 'month') {
                fetches.push(
                    dispatch(fetchAllAttendanceCard({ month: `${selectedYear}-${selectedMonth}` })).unwrap()
                );
            } else if (rangeFrom && rangeTo && rangeFrom <= rangeTo) {
                fetches.push(
                    dispatch(fetchAllAttendanceCard({ fromDate: rangeFrom, toDate: rangeTo })).unwrap()
                );
            }

            await Promise.all(fetches);
        } catch {
        } finally {
            setRefreshing(false);
        }
    }, [dispatch, refreshing, loading, dateMode, selectedYear, selectedMonth, rangeFrom, rangeTo]);

    const handleReset = useCallback(() => {
        const today = new Date();
        setSelectedYear(String(today.getFullYear()));
        setSelectedMonth(pad(today.getMonth() + 1));
        setDateMode('month');
        const { from, to } = defaultRange();
        setRangeFrom(from);
        setRangeTo(to);
        setStatusFilter('all');
        setSelectedEmployee(null);
        setHasSearched(false);
        setSort({ key: 'AttDate', dir: 'desc' });
    }, []);

    const toggleDateSort = useCallback(() => {
        setSort((s) => ({
            key: 'AttDate',
            dir: s.key === 'AttDate' && s.dir === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    const isDateActive = sort.key === 'AttDate';

    const handleTabChange = useCallback((tab) => {
        if (tab === 'summary') navigate('/hrm/attendance/details');
        else if (tab === 'details') navigate('/hrm/attendance/attendance-details');
        else if (tab === 'overview') navigate('/hrm/attendance/attendance-overview');
    }, [navigate]);

    const leaveTypeMap = useMemo(() => {
        const map = new Map();
        (leaveTypes || []).forEach((t) => map.set(String(t.id), t.name || t.code || `Type ${t.id}`));
        return map;
    }, [leaveTypes]);
    const resolveLeaveType = useCallback(
        (rawId) => leaveTypeMap.get(String(rawId)) || `Leave (${rawId})`,
        [leaveTypeMap]
    );

    function buildMonthDays(year, month) {
        const y = parseInt(year, 10);
        const m = parseInt(month, 10);
        const daysInMonth = new Date(y, m, 0).getDate();
        const list = [];
        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(y, m - 1, d);
            const dow = date.getDay();
            list.push({
                day: d,
                dayName: DAY_NAMES_FULL[dow],
                isSunday: dow === 0,
                ymd: `${y}-${pad(m)}-${pad(d)}`,
            });
        }
        return list;
    }

    const holidayMap = useMemo(() => {
        const map = new Map();
        holidays.forEach((h) => {
            if (h?.HAH_IS_ACTIVE === 'False') return;
            const ymd = toYMD(h.HAH_HOLIDAY_DATE);
            if (ymd) map.set(ymd, {
                name: h.HAH_HOLIDAY_NAME || 'Holiday',
                type: h.HAH_HOLIDAY_TYPE || '',
            });
        });
        return map;
    }, [holidays]);

    const leaveMap = useMemo(() => {
        const map = new Map();
        (leaves || []).forEach((l) => {
            const serviceNo = String(l.HLF_EMP_SERVICE_NO || '').trim();
            if (!serviceNo) return;
            const from = new Date(l.HLF_FROM_DATE);
            const to = new Date(l.HLF_TO_DATE);
            if (isNaN(from.getTime()) || isNaN(to.getTime())) return;

            const totalDays = parseFloat(l.HLF_TOTAL_DAYS || '0');
            const isHalf = l.HLF_HALF_DAY === 'Y' || totalDays === 0.5;

            const cursor = new Date(from); cursor.setHours(0, 0, 0, 0);
            const end = new Date(to); end.setHours(0, 0, 0, 0);
            while (cursor <= end) {
                const ymd = toYMD(cursor);
                if (ymd) map.set(`${serviceNo}|${ymd}`, {
                    leaveTypeId: l.HLF_LEAVE_TYPE_ID,
                    leaveTypeName: resolveLeaveType(l.HLF_LEAVE_TYPE_ID),
                    isHalf,
                    days: totalDays,
                    reason: l.HLF_REASON || '',
                });
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return map;
    }, [leaves, resolveLeaveType]);

    const attendanceMap = useMemo(() => {
        const map = new Map();
        (rows || []).forEach((r) => {
            const serviceNo = String(r.ServiceNo ?? '').trim();
            const ymd = toYMD(r.AttDate);
            if (serviceNo && ymd) map.set(`${serviceNo}|${ymd}`, r);
        });
        return map;
    }, [rows]);

    const todayStr = useMemo(() => todayYMD(), []);

    const monthDays = useMemo(() => {
        if (dateMode === 'range') {
            if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return [];
            const all = buildRangeDays(rangeFrom, rangeTo);
            return all.filter((d) => d.ymd <= todayStr);
        }

        const all = buildMonthDays(selectedYear, selectedMonth);
        const now = new Date();
        const curYear = now.getFullYear();
        const curMonth = now.getMonth() + 1;
        const curDay = now.getDate();

        const isCurrentMonth =
            Number(selectedYear) === curYear && Number(selectedMonth) === curMonth;

        if (!isCurrentMonth) return all;
        return all.filter((d) => d.day <= curDay);
    }, [dateMode, rangeFrom, rangeTo, selectedYear, selectedMonth, todayStr]);

    const allEmployees = useMemo(() => {
        const seen = new Set();
        const out = [];
        (rows || []).forEach((r) => {
            const serviceNo = String(r.ServiceNo ?? '').trim();
            if (!serviceNo || seen.has(serviceNo)) return;
            seen.add(serviceNo);

            const master = employeeMap.get(serviceNo) || {};
            out.push({
                serviceNo,
                name: r.Name || master.name || `Employee ${serviceNo}`,
                department: r.Department || master.department || '',
                location: r.Location || master.location || '',
                category: r.EmploymentCategory || master.category || '',
                designation: r.Designation || master.designation || '',
            });
        });
        return out.sort((a, b) => a.name.localeCompare(b.name));
    }, [rows, employeeMap]);

    const employeeOptions = useMemo(() => {
        return allEmployees.map((e) => ({
            employeeId: e.serviceNo,
            serviceNo: e.serviceNo,
            name: e.name,
            department: e.department,
            location: e.location,
            designation: e.designation,
            category: e.category,
        }));
    }, [allEmployees]);

    const flatRows = useMemo(() => {
        if (!hasSearched) return [];

        const empFilterId = selectedEmployee && !selectedEmployee.isAll
            ? String(selectedEmployee.serviceNo || selectedEmployee.employeeId || '').trim()
            : null;

        const out = [];
        for (const emp of allEmployees) {
            if (empFilterId && emp.serviceNo !== empFilterId) continue;

            for (const d of monthDays) {
                const key = `${emp.serviceNo}|${d.ymd}`;
                const att = attendanceMap.get(key);
                const leave = leaveMap.get(key);
                const holiday = holidayMap.get(d.ymd);
                const isToday = d.ymd === todayStr;

                const inTime = att?.InTime || '';
                const outTime = att?.OutTime || '';
                const halfDay = att?.HalfDay || '';
                const lateMinutes = att?.LateMinutes ?? null;
                const totalHours = att?.TotalHours != null
                    ? Number(att.TotalHours)
                    : (inTime && outTime ? computeHours(inTime, outTime) : 0);
                const normalHours = att?.NormalHours != null
                    ? Number(att.NormalHours)
                    : (totalHours > 0 ? Math.min(totalHours, 8) : 0);
                const otHours = att?.OTHours != null
                    ? Number(att.OTHours)
                    : (inTime && outTime ? computeOT(inTime, outTime) : 0);
                const preOT = att?.PreShiftOTHours != null ? Number(att.PreShiftOTHours) : 0;
                const postOT = att?.PostOTHours != null ? Number(att.PostOTHours) : 0;
                const prePostOT = att?.PrePostOTHours != null ? Number(att.PrePostOTHours) : 0;
                const holidayOT = att?.HolidayOTHours != null ? Number(att.HolidayOTHours) : 0;
                const sundayOT = att?.SundayOTHours != null ? Number(att.SundayOTHours) : 0;

                // Calendar conditions take priority over punch-based statuses.
                let status;
                let punchStatus = null;

                if (holiday) {
                    status = 'holiday';
                } else if (d.isSunday) {
                    status = 'sunday';
                } else if (halfDay === 'M' || halfDay === 'A') {
                    status = 'half-day';
                } else if (leave) {
                    status = leave.isHalf ? 'half-leave' : 'leave';
                } else {
                    status = getPunchStatus({ inTime, outTime, isToday });
                    punchStatus = status;
                }

                if (statusFilter !== 'all') {
                    if (statusFilter === 'leave') {
                        if (status !== 'leave' && status !== 'half-leave') continue;
                    } else if (status !== statusFilter) {
                        continue;
                    }
                }

                const statusLabel = status === 'half-day'
                    ? (halfDay === 'M' ? 'Half Day (Morning)' : 'Half Day (Afternoon)')
                    : PUNCH_LABELS[status] ||
                    (status === 'leave' || status === 'half-leave'
                        ? (status === 'half-leave' ? 'Half Day Leave' : 'Leave')
                        : status === 'holiday'
                            ? (holiday?.name || 'Holiday')
                            : status === 'sunday'
                                ? 'Sunday'
                                : status);

                out.push({
                    ...emp,
                    ...d,
                    att, leave, holiday, isToday,
                    inTime,
                    outTime,
                    halfDay,
                    lateMinutes,
                    totalHours,
                    normalHours,
                    otHours,
                    preOT,
                    postOT,
                    prePostOT,
                    holidayOT,
                    sundayOT,
                    status,
                    punchStatus,
                    statusLabel,
                    AttDate: d.ymd,
                    Name: att?.Name || emp.name,
                    ServiceNo: emp.serviceNo,
                    Department: att?.Department || emp.department || '',
                    Location: att?.Location || emp.location || '',
                    EmploymentCategory: att?.EmploymentCategory || emp.category || '',
                    Designation: att?.Designation || emp.designation || '',
                });
            }
        }
        return out;
    }, [
        hasSearched, allEmployees, monthDays, attendanceMap, leaveMap, holidayMap,
        statusFilter, selectedEmployee, todayStr,
    ]);

    const sortedRows = useMemo(() => {
        const list = [...flatRows];
        const { key, dir } = sort;
        const mult = dir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
            return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
        });
        return list;
    }, [flatRows, sort]);

    const isSingleEmployee = !!selectedEmployee && !selectedEmployee.isAll;

    const totals = useMemo(() => {
        if (!isSingleEmployee) return null;
        return sortedRows.reduce(
            (acc, r) => {
                acc.totalHours += Number(r.totalHours) || 0;
                acc.normalHours += Number(r.normalHours) || 0;
                acc.otHours += Number(r.otHours) || 0;
                acc.preOT += Number(r.preOT) || 0;
                acc.postOT += Number(r.postOT) || 0;
                acc.prePostOT += Number(r.prePostOT) || 0;
                acc.holidayOT += Number(r.holidayOT) || 0;
                acc.sundayOT += Number(r.sundayOT) || 0;
                return acc;
            },
            {
                totalHours: 0, normalHours: 0, otHours: 0,
                preOT: 0, postOT: 0, prePostOT: 0,
                holidayOT: 0, sundayOT: 0,
            }
        );
    }, [sortedRows, isSingleEmployee]);

    const handleExport = useCallback(() => {
        if (!sortedRows.length) return;

        exportAttendanceOverviewToExcel({
            rows: sortedRows.map((r) => ({
                ServiceNo: r.ServiceNo,
                Name: r.Name,
                Date: r.AttDate,
                Day: r.dayName,
                InTime: r.inTime,
                OutTime: r.outTime,
                HalfDay: r.halfDay || '',
                LateMinutes: r.lateMinutes ?? r.att?.LateMinutes ?? 0,
                TotalHours: r.totalHours,
                NormalHours: r.normalHours,
                OTHours: r.otHours,
                PreOT: r.preOT ?? 0,
                PostOT: r.postOT ?? 0,
                PrePostOT: r.prePostOT ?? 0,
                HolidayOT: r.holidayOT ?? 0,
                SundayOT: r.sundayOT ?? 0,
                Status: r.statusLabel || r.punchStatus || r.status,
                Department: r.Department,
                Location: r.Location,
                Category: r.EmploymentCategory,
            })),

            filters: {
                'Date Filter':
                    dateMode === 'month'
                        ? `${MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label || selectedMonth} ${selectedYear}`
                        : `${rangeFrom} → ${rangeTo}`,
                Show: statusFilter,
                Employee:
                    selectedEmployee && !selectedEmployee.isAll
                        ? `${selectedEmployee.serviceNo || ''} — ${selectedEmployee.name || ''}`.trim()
                        : 'All Employees',
                'Total Records': sortedRows.length,
            },
        });
    }, [
        sortedRows, selectedYear, selectedMonth, selectedEmployee, statusFilter,
        dateMode, rangeFrom, rangeTo,
    ]);

    const rowClass = (r) => {
        switch (r.status) {
            case 'full-day': return 'bg-white hover:bg-gray-50';
            case 'present': return 'bg-teal-50 hover:bg-teal-100';
            case 'mismatch': return 'bg-orange-50 hover:bg-orange-100';
            case 'missing-punch': return 'bg-fuchsia-50 hover:bg-fuchsia-100';
            case 'no-record': return 'bg-slate-100 hover:bg-slate-200';
            case 'half-day': return 'bg-indigo-50 hover:bg-indigo-100';
            case 'leave': return 'bg-blue-100 hover:bg-blue-200';
            case 'half-leave': return 'bg-cyan-50 hover:bg-cyan-100';
            case 'holiday': return 'bg-yellow-100 hover:bg-yellow-100';
            case 'sunday': return 'bg-rose-50 hover:bg-rose-100';
            default: return 'bg-white hover:bg-slate-50';
        }
    };

    const rowBgSolid = (r) => {
        switch (r.status) {
            case 'full-day': return 'bg-white';
            case 'present': return 'bg-teal-50';
            case 'mismatch': return 'bg-orange-50';
            case 'missing-punch': return 'bg-fuchsia-50';
            case 'no-record': return 'bg-slate-100';
            case 'half-day': return 'bg-indigo-50';
            case 'leave': return 'bg-blue-100';
            case 'half-leave': return 'bg-cyan-50';
            case 'holiday': return 'bg-yellow-100';
            case 'sunday': return 'bg-rose-50';
            default: return 'bg-white';
        }
    };

    const statusBadge = (r) => {
        if (r.status === 'leave') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-400">
                    {r.leave?.leaveTypeName || 'Leave'}
                </span>
            );
        }

        if (r.status === 'half-leave') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-400">
                    Half Day – {r.leave?.leaveTypeName || 'Leave'}
                </span>
            );
        }

        if (r.status === 'holiday') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-yellow-100 text-black ring-1 ring-inset ring-yellow-600">
                    {r.holiday?.name || 'Holiday'}
                </span>
            );
        }

        if (r.status === 'sunday') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-400">
                    Sunday
                </span>
            );
        }

        if (r.halfDay === 'M' || r.halfDay === 'A') {
            const label = r.halfDay === 'M' ? 'Half Day (Morning)' : 'Half Day (Afternoon)';
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-400">
                    {label}
                </span>
            );
        }

        const BADGES = {
            'full-day': 'bg-emerald-50 text-emerald-700 ring-emerald-400',
            present: 'bg-teal-50 text-teal-700 ring-teal-400',
            mismatch: 'bg-orange-50 text-orange-700 ring-orange-400',
            'missing-punch': 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-400',
            'no-record': 'bg-slate-100 text-slate-600 ring-slate-400',
            'half-day': 'bg-indigo-50 text-indigo-700 ring-indigo-400',
            sunday: 'bg-rose-50 text-rose-700 ring-rose-400',
        };

        const label = r.status === 'sunday'
            ? 'Sunday'
            : PUNCH_LABELS[r.status] || r.statusLabel || '—';
        const badgeClass = BADGES[r.status] || 'bg-slate-100 text-slate-500 ring-slate-400';

        return (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium ring-1 ring-inset ${badgeClass}`}>
                {label}
            </span>
        );
    };

    const SortHeader = ({ colKey, label, align = 'left', width, info, sticky, left }) => {
        const active = sort.key === colKey;
        const arrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
        return (
            <th
                onClick={() => setSort((s) => ({
                    key: colKey,
                    dir: s.key === colKey && s.dir === 'asc' ? 'desc' : 'asc',
                }))}
                style={{
                    width,
                    ...(sticky ? { position: 'sticky', left } : {}),
                }}
                className={`px-2 py-2.5 font-semibold border-b border-slate-200 bg-slate-50 cursor-pointer select-none
                ${sticky ? 'z-40' : ''}
                ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
            >
                <span className="inline-flex items-center gap-1">
                    {label}
                    <span className="text-[8px] text-slate-400">{arrow}</span>
                    {info && (
                        <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
                            <InfoTip>{info}</InfoTip>
                        </span>
                    )}
                </span>
            </th>
        );
    };

    const colGroup = (
        <colgroup>
            {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
        </colgroup>
    );

    return (
        <div className="p-4 md:p-0 space-y-2 bg-slate-50/40 min-h-screen">
            <Breadcrumb
                trail={['eHRM', 'Attendance & Shifts', 'Attendance Overview']}
                title="Attendance Overview"
                action={
                    <button onClick={handleExport} disabled={!sortedRows.length || loading}
                        className={BREADCRUMB_EXPORT_BTN}>
                        <FileSpreadsheet size={15} /> Export to Excel
                    </button>
                }
            />

            <div className="border-b border-slate-200 bg-white rounded-t-xl px-4 pt-1 shadow-sm">
                <nav className="flex gap-0" aria-label="Tabs">
                    <button onClick={() => handleTabChange('summary')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isSummaryTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <LayoutGrid size={16} /> Attendance Summary
                    </button>
                    <button onClick={() => handleTabChange('details')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isDetailsTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <List size={16} /> Attendance Details
                    </button>
                    <button onClick={() => handleTabChange('overview')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isOverviewTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <Users size={16} /> Attendance Overview
                    </button>
                </nav>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-end gap-3">
                    <div className="flex items-end gap-3 md:w-2/5">
                        <div className="shrink-0">
                            <span className={LABEL}>Date Filter</span>
                            <div className="inline-flex h-9 items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
                                {['month', 'range'].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => {
                                            setDateMode(m);
                                            if (m === 'range' && (!rangeFrom || !rangeTo)) {
                                                const { from, to } = defaultRange();
                                                setRangeFrom(from);
                                                setRangeTo(to);
                                            }
                                        }}
                                        className={`px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${dateMode === m
                                            ? 'bg-white text-slate-900 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                    >
                                        {m === 'month' ? 'Month' : 'Range'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {dateMode === 'month' ? (
                            <>
                                <div className="flex-1 min-w-0">
                                    <span className={LABEL}>Year</span>
                                    <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className={FIELD}>
                                        {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className={LABEL}>Month</span>
                                    <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className={FIELD}>
                                        {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex-1 min-w-0">
                                    <span className={LABEL}>From</span>
                                    <input
                                        type="date"
                                        value={rangeFrom}
                                        max={rangeTo || undefined}
                                        onChange={(e) => setRangeFrom(e.target.value)}
                                        className={FIELD}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className={LABEL}>To</span>
                                    <input
                                        type="date"
                                        value={rangeTo}
                                        min={rangeFrom || undefined}
                                        onChange={(e) => setRangeTo(e.target.value)}
                                        className={FIELD}
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex-[1.4] min-w-[180px]">
                            <span className={LABEL}>Show</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FIELD}>
                                {STATUS_FILTERS.map((f) => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-end gap-2 md:flex-1 md:justify-end">
                        <div className="flex-1 min-w-[220px] max-w-md">
                            <span className={LABEL}>Employee</span>
                            <EmployeeSearch
                                value={selectedEmployee}
                                onSelect={setSelectedEmployee}
                                employees={employeeOptions}
                                loading={loading}
                                showAllOption
                            />
                        </div>
                        <button onClick={handleSearch} disabled={loading}
                            className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap shadow-sm">
                            <Search size={14} /> {loading ? 'Loading...' : 'Search'}
                        </button>
                        <button onClick={handleReset}
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap">
                            <RotateCcw size={14} /> Reset
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing || loading}
                            title="Refresh data"
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

            <div className="p-4 md:p-0 space-y-2 bg-slate-50/40 min-h-screen">
                <div className="flex flex-wrap items-center gap-3 rounded-t-xl border-b border-slate-200/70 bg-gradient-to-r from-white to-slate-50/40 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                        <CalendarRange size={16} className="text-primary-600" />
                        <h3 className="text-sm font-semibold text-slate-800">Attendance Overview</h3>
                    </div>

                    <button
                        type="button"
                        onClick={toggleDateSort}
                        title={
                            isDateActive && sort.dir === 'asc'
                                ? 'Sorted oldest → newest. Click for newest → oldest.'
                                : 'Sorted newest → oldest. Click for oldest → newest.'
                        }
                        className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-all active:scale-[0.98] ${isDateActive
                            ? 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm hover:bg-primary-100'
                            : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                    >
                        {isDateActive ? (
                            sort.dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />
                        ) : (
                            <ArrowUpDown size={13} className="text-slate-400" />
                        )}
                        <span className="tracking-tight">Date</span>
                        {isDateActive && (
                            <span className="ml-0.5 text-[10px] font-bold tracking-widest opacity-60">
                                {sort.dir === 'asc' ? 'ASC' : 'DESC'}
                            </span>
                        )}
                    </button>
                </div>

                <div className="rounded-b-xl overflow-clip bg-white">
                    <div
                        ref={headerRef}
                        className="sticky z-30 overflow-hidden bg-slate-50"
                        style={{ top: STICKY_TOP }}
                    >
                        <table
                            className="text-xs border-separate border-spacing-0"
                            style={{ minWidth: TABLE_MIN_WIDTH, width: '100%', tableLayout: 'fixed' }}
                        >
                            {colGroup}
                            <thead>
                                <tr className="bg-slate-50 text-slate-500">
                                    {COLUMNS.map((c) => (
                                        <SortHeader
                                            key={c.key}
                                            colKey={c.key}
                                            label={c.label}
                                            width={c.width}
                                            align={c.align}
                                            info={c.info}
                                            sticky={c.sticky}
                                            left={c.left}
                                        />
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>

                    <div
                        ref={tableScrollRef}
                        onScroll={onTableScroll}
                        className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        <table
                            className="text-xs border-separate border-spacing-0"
                            style={{ minWidth: TABLE_MIN_WIDTH, width: '100%', tableLayout: 'fixed' }}
                        >
                            {colGroup}
                            <tbody>
                                {sortedRows.map((r) => (
                                    <tr key={`${r.ServiceNo}|${r.AttDate}`} className={`group ${rowClass(r)}`}>
                                        <td
                                            style={{ position: 'sticky', left: 0, zIndex: 10 }}
                                            className={`px-2 py-2 text-center text-slate-500 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.ServiceNo}
                                        </td>

                                        <td
                                            style={{ position: 'sticky', left: 110, zIndex: 10 }}
                                            className={`px-2 py-2 text-slate-900 font-medium border-b border-slate-100 ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.Name}
                                        </td>

                                        <td
                                            style={{ position: 'sticky', left: 310, zIndex: 10 }}
                                            className={`px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums font-medium ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.AttDate}
                                        </td>

                                        <td
                                            style={{ position: 'sticky', left: 430, zIndex: 10 }}
                                            className={`px-2 py-2 border-b border-slate-100 ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            <span className={`font-semibold ${r.isSunday ? 'text-rose-600' : 'text-slate-600'}`}>
                                                {r.dayName}
                                            </span>
                                        </td>

                                        <td
                                            style={{ position: 'sticky', left: 530, zIndex: 10 }}
                                            className={`px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.inTime || '—'}
                                        </td>

                                        <td
                                            style={{ position: 'sticky', left: 620, zIndex: 10 }}
                                            className={`px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.outTime || '—'}
                                        </td>

                                        <td className="px-2 py-2 text-center border-b border-slate-100">
                                            {statusBadge(r)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtLateHours(r.lateMinutes)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.totalHours)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.normalHours)}
                                        </td>

                                        <td className="px-2 py-2 text-center border-b border-slate-100 tabular-nums">
                                            {r.otHours > 0 ? (
                                                <span className="font-semibold text-emerald-700">{fmtHours(r.otHours)}</span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.preOT)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.postOT)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.holidayOT)}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {fmtHours(r.sundayOT)}
                                        </td>

                                        <td className="px-2 py-2 text-slate-600 border-b border-slate-100">
                                            {r.Department || '—'}
                                        </td>

                                        <td className="px-2 py-2 text-slate-600 border-b border-slate-100">
                                            {r.Location || '—'}
                                        </td>

                                        <td className="px-2 py-2 text-center text-slate-600 border-b border-slate-100">
                                            {r.EmploymentCategory || '—'}
                                        </td>
                                    </tr>
                                ))}
                                {sortedRows.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-slate-400">
                                            {loading ? 'Loading attendance data…'
                                                : hasSearched
                                                    ? 'No records match the current filters.'
                                                    : dateMode === 'range'
                                                        ? 'Pick a date range and click Search.'
                                                        : 'Pick a month and click Search.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            {totals && sortedRows.length > 0 && (
                                <tfoot>
                                    <tr className="bg-slate-100 text-slate-800 font-semibold shadow-[0_-2px_0_0_rgb(203_213_225)]">
                                        <td style={{ position: 'sticky', left: 0, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td style={{ position: 'sticky', left: 110, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td style={{ position: 'sticky', left: 310, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td style={{ position: 'sticky', left: 430, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td style={{ position: 'sticky', left: 530, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td style={{ position: 'sticky', left: 620, zIndex: 10 }} className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />

                                        {/* Status + Late Hours — non-sticky empties (aligns with body) */}
                                        <td className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />
                                        <td className="bg-slate-100 px-2 py-2.5 border-t-2 border-slate-300" />

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.totalHours)}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.normalHours)}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {totals.otHours > 0
                                                ? <span className="text-emerald-700">{fmtHours(totals.otHours)}</span>
                                                : '—'}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.preOT)}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.postOT)}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.holidayOT)}
                                        </td>

                                        <td className="bg-slate-100 px-2 py-2.5 text-center tabular-nums border-t-2 border-slate-300">
                                            {fmtHours(totals.sundayOT)}
                                        </td>

                                        <td colSpan={3} className="border-t-2 border-slate-300 bg-slate-100" />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div
                        ref={barRef}
                        onScroll={onBarScroll}
                        className="sticky z-30 overflow-x-auto overflow-y-hidden bg-white/95 border-t border-slate-200 rounded-b-xl"
                        style={{
                            bottom: STICKY_BOTTOM,
                            height: 16,
                            display: bar.hasOverflow && sortedRows.length > 0 ? 'block' : 'none',
                        }}
                        aria-hidden="true"
                    >
                        <div style={{ width: bar.scrollWidth, height: 1 }} />
                    </div>
                </div>
            </div>
        </div>
    );
}